import React, { useEffect, useState } from "react";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
// currency conversion
import { useCurrencyUtils } from "../../utils/currencyUtils";

const SellerInvoices = () => {
  const { showSuccess, showError } = useToast();
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [withdraw, setWithdraw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleWithdrawRequest = () => {
    // Open confirmation toast first
    setShowConfirm(true);
  };

  const handleConfirmWithdraw = async () => {
    if (withdraw) return;
    try {
      setWithdraw(true);
      setError(null);
      await api.post(`/sellerwithdrawalinvoices/withdrawrequest`);
      showSuccess(
        translate("sellerInvoices.messages.withdrawRequestSubmitted"),
      );
    } catch (err) {
      setError(translate("sellerInvoices.messages.withdrawRequestFailed"));
      showError(translate("sellerInvoices.messages.withdrawRequestFailed"));
    } finally {
      setWithdraw(false);
      setShowConfirm(false);
    }
  };

  useEffect(() => {
    const fetchSellerInvoices = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/sellerwithdrawalinvoices/paginated`, {
          params: { orderBy: "id", sort: "desc", page: 1, pageSize: 20 },
        });

        const data = response?.data?.data || response?.data || [];
        setInvoices(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(translate("sellerInvoices.messages.failedToLoad"));
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSellerInvoices();
  }, []);

  const getStatusText = (status) => {
    if (!status) return "-";
    const statusMap = {
      Paid: translate("sellerInvoices.status.paid"),
      Pending: translate("sellerInvoices.status.pending"),
      "Partial Payment": translate("sellerInvoices.status.partialPayment"),
      Cancelled: translate("sellerInvoices.status.cancelled"),
      Refunded: translate("sellerInvoices.status.refunded"),
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "-") return "-";
    return new Date(dateString).toLocaleDateString(
      language === "ar" ? "ar-AE" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      },
    );
  };

  return (
    <div
      className="bg-card rounded-lg shadow "
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
        {/* Standardized Header Container with fixed height logic */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-4 min-h-[120px] lg:min-h-[72px]">
          {/* Left Side: Heading */}
          <div className="flex flex-col justify-center">
            <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap">
              {translate("sellerInvoices.heading")}
            </h2>
          </div>

          {/* Right Side: Action Button */}
          <div className="flex items-center ms-auto lg:ms-0">
            <button
              onClick={handleWithdrawRequest}
              disabled={withdraw}
              className={`px-4 py-1.5 rounded-full font-semibold text-[10px] sm:text-xs shadow-md transition-all active:scale-95 whitespace-nowrap ${
                withdraw
                  ? "bg-white/50 text-primary/50 cursor-not-allowed"
                  : "bg-white text-primary hover:bg-white/90"
              }`}
            >
              {withdraw
                ? translate("sellerInvoices.buttons.submitting")
                : translate("sellerInvoices.buttons.requestWithdraw")}
            </button>
          </div>
        </div>
      </div>

      {showConfirm && (
        <>
          {/* 1. Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10000,
            }}
            onClick={() => setShowConfirm(false)}
          />

          {/* 2. Centering Wrapper */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10001,
              pointerEvents: "none",
            }}
          >
            {/* 3. The Modal Box */}
            <div
              className="bg-white rounded-lg shadow-2xl border p-6"
              style={{
                pointerEvents: "auto",
                width: "90%",
                maxWidth: "400px",
              }}
              dir={isRTL ? "rtl" : "ltr"}
            >
              <div className="mb-5">
                <h6 className="fw-bold text-dark m-0">
                  {translate("sellerInvoices.messages.confirmWithdraw")}
                </h6>
              </div>

              {/* 4. Button Container: Forced to the right */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: isRTL ? "flex-start" : "flex-end", // Alignment logic
                  width: "100%",
                }}
              >
                <button
                  onClick={() => setShowConfirm(false)}
                  className="btn btn-light border px-4 py-2 text-sm"
                  style={{ borderRadius: "6px" }}
                >
                  {translate("sellerInvoices.buttons.cancel")}
                </button>

                <button
                  onClick={handleConfirmWithdraw}
                  disabled={withdraw}
                  className={`btn px-4 py-2 text-sm font-semibold`}
                  style={{
                    backgroundColor: "transparent",
                    color: withdraw
                      ? "#6c757d"
                      : "var(--primary-color, #0d6efd)",
                    border: `1px solid ${withdraw ? "#dee2e6" : "var(--primary-color, #0d6efd)"}`,
                    borderRadius: "6px",
                    minWidth: "100px",
                  }}
                >
                  {withdraw
                    ? "..."
                    : translate("sellerInvoices.buttons.confirm")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="p-6 text-sm text-gray-500">
          {translate("sellerInvoices.messages.loading")}
        </div>
      ) : error ? (
        <div className="p-6 text-sm text-red-600">{error}</div>
      ) : invoices.length === 0 ? (
        <div className="p-6 text-sm text-gray-500">
          {translate("sellerInvoices.messages.noInvoicesFound")}
        </div>
      ) : (
        <div className="overflow-x-auto px-3 py-6">
          <table className="min-w-full border-separate border-spacing-0">
            <thead>
              <tr className="bg-page">
                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right rounded-l-lg" : "text-left rounded-l-lg"
                  } text-xs font-medium text-white uppercase tracking-wider border-b border-default`}
                >
                  {translate("sellerInvoices.tableHeaders.vehicle")}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider border-b border-default`}
                >
                  {translate("sellerInvoices.tableHeaders.totalAmount")}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider border-b border-default`}
                >
                  {translate("sellerInvoices.tableHeaders.status")}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right rounded-r-lg" : "text-left rounded-r-lg"
                  } text-xs font-medium text-white uppercase tracking-wider border-b border-default`}
                >
                  {translate("sellerInvoices.tableHeaders.date")}
                </th>
              </tr>
            </thead>

            <tbody className="border-b border-default">
              {invoices.map((inv) => {
                // Get vehicle name from sellerInvoiceDetails array
                const vehicleName =
                  inv?.sellerInvoiceDetails?.[0]?.vehicle?.name ?? "-";
                const id = inv?.id ?? "-";
                const date = inv?.createdAt ?? "-";
                const amount = inv?.totalAmount ?? 0;
                const status = inv?.invoicePaymentStatus?.name ?? "-";

                return (
                  <tr
                    key={inv?.id}
                    className="border-b border-default hover:bg-page/70"
                  >
                    <td className="px-4 py-2 text-sm text-white">
                      {vehicleName}
                    </td>

                    <td className="px-4 py-2 text-sm font-medium text-white/70">
                      {displayPriceWithSymbol(convertPrice(amount))}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          status === "Paid"
                            ? "bg-green-700 text-white"
                            : status === "Pending"
                              ? "bg-yellow-700 text-white"
                              : status === "Partial Payment"
                                ? "bg-blue-700 text-white"
                                : status === "Cancelled"
                                  ? "bg-red-700 text-white"
                                  : status === "Refunded"
                                    ? "bg-purple-700 text-white"
                                    : "bg-gray-700 text-white"
                        }`}
                      >
                        {getStatusText(status)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-white/70">
                      {formatDate(date)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SellerInvoices;
